import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import throttle from 'lodash-es/throttle';
import debounce from 'lodash-es/debounce';
import IdleTimer from 'react-idle-timer';
import apiActions from '../../common/actions/apiActions';
import embedActions from '../actions/embedActions';
import { preloadSlideImages } from '../../common/actions/preloadActions';
import * as navigationActions from '../../common/actions/navigationActions';
import handleError from '../../common/actions/errorActions';
import styles from '../styles';
import utils from '../../common/utils';
import MediaRecorder from './MediaRecorder';
import MediaViewer from './MediaViewer';
import AppLoader from '../../common/components/AppLoader';
import keyboard from '../../common/keyboard';
import { getHashIndex } from '../../common/lib/urls';
import fabric from '../../common/fabric';
import { database } from '../../common/firebase/setup';
import {
  onValue,
  off,
  ref,
  query,
  equalTo,
  orderByChild,
} from 'firebase/database';

import smalltalk from '../../vendor/smalltalk';
import { Styles } from '../../common/components/Styles';
import { migrateSlideContent } from '../../common/migrations';
import {
  handleFirebaseError,
  migrateFirebasePresentationToArray,
} from '../../common/firebase/utils';
import Viewer from './Viewer';
import ShareDialog from './dialog/ShareDialog';
import TopControls from './TopControls';
import SlidesList from './SlidesList';

const DEFAULT_TOP_MARGIN = 0;
const DEFAULT_BOTTOM_MARGIN = 0;

const LEFT_MOUSE_ZONE = 165;
const VERTICAL_MOUSE_ZONE = 200;

const TOP_CONTROLS_HEIGHT = 0;

export const ImageContext = React.createContext({});

let autoTimeout = null;

const updateHistory = debounce(hashIndex => {
  window.history.pushState({}, null, `#${hashIndex}`);
}, 250);

const debounceDimensions = debounce(
  (index, setDimensions, getDimensions, slideChangedBy) => {
    setDimensions(getDimensions());
  },
  500,
  { leading: false, trailing: true },
);
const topOffset = '38px';
let isWindowDraft;

const Container = props => {
  const lastMouseDown = React.useRef(null);
  const [imageStatus, setImageStatus] = React.useState();
  const [showViewer, setShowViewer] = React.useState(false);

  const delay = Math.max(parseInt(utils.getUrlParameter('delay'), 10) || 5, 3); // 2 seconds is the max value of a transition thus delay should not be under 3 (default is 5)
  const loop =
    utils.getUrlParameter('loop') === null ||
    (utils.getUrlParameter('loop') !== 'false' &&
      utils.getUrlParameter('loop') !== '0');

  const [auto, setAuto] = useState(
    utils.getUrlParameter('auto') !== null &&
      utils.getUrlParameter('auto') !== 'false' &&
      utils.getUrlParameter('auto') !== '0',
  );

  const debouncedRefreshPresentation = throttle(
    data => {
      props.actions.refreshPresentation(data);
      if (data.slides) {
        props.actions.preloadSlideImages(data.slides, []);
      }
    },
    1000,
    { trailing: true, leading: true },
  );
  const [visibleShare, setVisibleShare] = useState(false);

  const clickShare = () => {
    setVisibleShare(true);
  };

  const closeShare = () => {
    setVisibleShare(false);
  };

  React.useEffect(() => {
    isWindowDraft =
      new URLSearchParams(window.location.search).get('draft') === '1';
    const refDatabase = ref(database, `presentations/${window.presentationId}`);

    const refImage = ref(database, `images/${window.presentationId}`);

    if (isWindowDraft && (props.team || props.user)) {
      onValue(
        refImage,
        snapshot => {
          setImageStatus(snapshot.val());
        },
        handleFirebaseError,
      );

      onValue(
        refDatabase,
        snapshot => {
          const presentation = snapshot.val();
          const _slides = migrateFirebasePresentationToArray({
            presentation,
            transitions: props.transitions,
          });
          debouncedRefreshPresentation({
            ...presentation,
            slides: _slides,
            updatedAt: presentation.updated_at,
            publishedAt: presentation.last_published,
            publishedVersion: presentation.published_version,
            name: presentation.name,
            hideControls: presentation.hide_controls,
            scrollableViewer: presentation.scrollable_viewer,
          });
          document.title = `${presentation.name} · Ludus`;
        },
        handleFirebaseError,
      );
      const ownerId = props.team ? props.team.id : props.user.id;
      const refSymbols = query(
        ref(database, 'symbols'),
        orderByChild('owner_id'),
        equalTo(ownerId),
      );

      onValue(
        refSymbols,
        snapshot => {
          const symbols = Object.values(snapshot.val() || {}).map(symbol => {
            fabric.LudusSymbol.register(
              symbol.id,
              symbol.name || 'Symbol',
              migrateSlideContent(symbol.content),
            );
            return {
              id: symbol.id,
              content: JSON.stringify(migrateSlideContent(symbol.content)),
              name: symbol.name,
            };
          });
          props.actions.refreshSymbols(symbols);
          if (symbols) {
            props.actions.preloadSlideImages([], symbols);
          }
        },
        handleFirebaseError,
      );

      return () => {
        off(refDatabase, 'value');
        off(refSymbols, 'value');
        off(refImage, 'value');
      };
    } else if (window.isEditable) {
      onValue(
        refDatabase,
        snapshot => {
          // const { slides: firebaseSlides, ...presentation } = snapshot.val();
          const presentation = snapshot.val();

          const shouldReloadSlides =
            props.publishedVersion !== '' &&
            props.publishedVersion !== presentation.published_version;
          const refreshParams = {
            updatedAt: presentation.updated_at,
            publishedAt: presentation.last_published,
            scrollableViewer: presentation.scrollable_viewer,
            hideControls: presentation.hide_controls,
            version: presentation.version,
            publishedVersion: presentation.published_version,
          };
          if (isWindowDraft || shouldReloadSlides) {
            refreshParams.slides = migrateFirebasePresentationToArray({
              presentation,
              transitions: props.transitions,
            });
          }
          debouncedRefreshPresentation(refreshParams);
        },
        handleFirebaseError,
      );
      return () => {
        off(refDatabase, 'value');
      };
    }
  }, [props.team, props.user, props.publishedVersion]);

  React.useEffect(() => {
    const hashIndex = props.index + 1;
    if (!props.scrollableViewer) {
      if (props.index !== previousSlideIndex) {
        setPreviousSlideIndex(props.index);
      }
      if (props.initialized) {
        if (previousSlideIndex !== props.index) {
          const prevHashIndex = getHashIndex(
            window.location.hash,
            props.slides.length,
          );

          if (prevHashIndex !== hashIndex) {
            // Setting location hash with History API prevents some quirks on Safari mobile,
            // such as the navigation bar showing up in landscape mode
            window.history.pushState({}, null, `#${hashIndex}`);
            notifyOtherWindows();
          }
          auto && doAutoPlay();
        }
      }
    } else {
      if (props.initialized && props.firebaseInitialized) {
        updateHistory(hashIndex);
      }
    }
  }, [props.index, props.initialized]);

  const controlsAlwaysVisible = utils.isEmbed() && !props.isFullScreen;
  const [showSlides, setShowSlides] = useState(false);
  const [insideTopControls, setInsideTopControls] = useState(false);
  const [dimensions, setDimensions] = useState(null);
  const [previousSlideIndex, setPreviousSlideIndex] = useState(-1);
  const [controlsVisible, setControlsVisible] = useState(() => true);

  const [isOpenSpeakerView, setIsOpenSpeakerView] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);

  const [ready, setReady] = useState(false);
  const idleTimer = React.useRef(null);
  const mediaRecorder = React.useRef(null);
  const mediaViewer = React.useRef(null);

  const getDimensions = () => {
    const containerWidth = window.documentSize.width;
    const containerHeight = window.documentSize.height;
    const containerRatio = containerWidth / containerHeight;
    const leftOffset = showSlides ? 145 : 0;
    const topOffset = 0;
    const padding =
      props.isFullScreen || utils.isMobileDevice() || props.scrollableViewer
        ? 0
        : 32;
    const documentWidth = document.body.clientWidth - padding;

    let topMargin;
    let bottomMargin;

    if (controlsAlwaysVisible) {
      topMargin = TOP_CONTROLS_HEIGHT;
      bottomMargin = DEFAULT_BOTTOM_MARGIN;
    } else {
      topMargin = DEFAULT_TOP_MARGIN;
      bottomMargin = DEFAULT_BOTTOM_MARGIN;
    }

    const horizontalOffset = leftOffset;
    const verticalOffset = (topMargin - bottomMargin) / 2 + topOffset / 2;

    const documentHeight =
      document.body.clientHeight - (topMargin + bottomMargin) - padding;
    const documentRatio = documentWidth / documentHeight;

    const scale = props.scrollableViewer
      ? documentWidth / containerWidth
      : documentRatio > containerRatio
      ? documentHeight / containerHeight
      : documentWidth / containerWidth;

    let orientation;
    if ('matchMedia' in window) {
      if (window.matchMedia('(orientation: portrait)').matches) {
        orientation = 'vertical';
      } else {
        orientation = 'horizontal';
      }
    } else if (documentWidth < documentHeight) {
      orientation = 'vertical';
    } else {
      orientation = 'horizontal';
    }

    return {
      scale,
      orientation,
      containerWidth,
      containerHeight,
      horizontalOffset,
      verticalOffset,
      width: documentWidth,
      height: documentHeight,
      zoom: document.documentElement.clientWidth / window.innerWidth,
    };
  };
  useEffect(() => {
    const dimensions = getDimensions();

    setDimensions(dimensions);
  }, [
    showSlides,
    props.isFullScreen,
    props.initialized,
    props.scrollableViewer,
  ]);

  useEffect(() => {
    props.actions
      .initialize()
      .then(() => {
        setShowViewer(true);
        document.addEventListener('fullscreenchange', onFullScreenChange);
        document.addEventListener('mozfullscreenchange', onFullScreenChange);
        document.addEventListener('webkitfullscreenchange', onFullScreenChange);
        document.addEventListener('keydown', onKeyDown);

        // Expose a simple third-party API for slide navigation,
        // for example when embedding a presentation in an iframe
        window.addEventListener('message', e => {
          if (e.data.type === 'previousSlide') {
            props.actions.dispatchPreviousSlide();
          }
          if (e.data.type === 'nextSlide') {
            props.actions.dispatchNextSlide();
          }
          if (e.data.type === 'goToSlide') {
            props.actions.dispatchGoToSlide(e.data.value, false);
          }
        });

        window.addEventListener('play', () => {
          setAuto(true);
          doAutoPlay();
        });

        window.addEventListener('stop', () => {
          setAuto(false);
          stopAutoPlay();
        });

        window.addEventListener('playerReady', () => {
          setReady(true);
        });

        window.addEventListener('playerFinished', () => {
          setAuto(false);
          setReady(false);
          stopAutoPlay();
        });

        // one second timeout so we're sure we're really ready
        setTimeout(() => {
          window.player.ready = true;
          window.dispatchEvent(new Event('playerReady'));
          if (auto) {
            doAutoPlay();
          }
        }, 1000);

        notifyOtherWindows();
      })
      .catch(props.actions.handleError);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onWindowResize);
    window.addEventListener('hashchange', onHashChange);
    return function cleanup() {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('resize', onWindowResize);
    };
  });

  const onKeyDown = e => {
    const key = keyboard.keyFromEvent(e);
    if (key.is(keyboard.PAGE_UP, keyboard.UP, keyboard.LEFT)) {
      e.preventDefault();
      props.actions.dispatchPreviousSlide();
    }
    if (key.is(keyboard.PAGE_DOWN, keyboard.DOWN, keyboard.RIGHT)) {
      e.preventDefault();
      props.actions.dispatchNextSlide();
    }
    if (key.is(keyboard.SPACE)) {
      if (mediaRecorder.current) {
        mediaRecorder.current.toggleStartStop();
      }
      if (mediaViewer.current) {
        mediaViewer.current.toggleStartStop();
      }
    }
  };

  const onFullScreenChange = () => {
    if (
      document.fullScreen ||
      document.mozFullScreen ||
      document.webkitIsFullScreen
    ) {
      props.actions.didEnterFullScreen();
    } else {
      props.actions.didExitFullScreen();
    }
  };

  const onWindowResize = () => {
    debounceDimensions(
      props.index,
      setDimensions,
      getDimensions,
      props.slideChangedBy,
    );
  };
  const onMouseMove = e => {
    // Toggle cursor
    if (lastMouseDown.current && Date.now() - lastMouseDown.current < 1250) {
      // if you just clicked to hide the controls we don't show it back immediatly
      // it also fixes a bug on ipad where the Move event is dispatched even if you only tapped
      return false;
    }

    if (props.hideControls) {
      !cursorVisible && setCursorVisible(true);
      return;
    }
    // Show controls
    if (e.buttons || utils.isMobileDevice()) {
      // prevent the side bar to appears when you drag the top controls
      return false;
    }
    !cursorVisible && setCursorVisible(true);

    const verticalTop = dimensions.height / 2 - VERTICAL_MOUSE_ZONE / 2;
    const verticalBottom = dimensions.height / 2 + VERTICAL_MOUSE_ZONE / 2;
    if (
      !showSlides &&
      e.clientX < LEFT_MOUSE_ZONE * 0.25 &&
      e.clientY > verticalTop &&
      e.clientY < verticalBottom
    ) {
      toggleSlides(true);
    }
    if (showSlides && e.clientX > LEFT_MOUSE_ZONE) {
      toggleSlides(false);
    }

    if (e.movementX !== 0 || e.movementY !== 0) {
      if (!controlsVisible && !props.scrollableViewer) {
        setControlsVisible(true);
      }
    }
  };

  const onTap = e => {
    // Tap event is only triggered on touch devices,
    // and only if a swipe hasn't occured.
    lastMouseDown.current = Date.now();
    if (utils.isMobileDevice()) {
      // avoid double trigger of toggle on iPad
      toggleControls();
    }
  };

  const onClick = () => {
    lastMouseDown.current = Date.now();
    if (!utils.isMobileDevice()) {
      toggleControls();
    }
  };

  const toggleControls = () => {
    setControlsVisible(!controlsVisible);
  };

  const toggleSlides = visible => {
    setShowSlides(visible);
  };

  const onHashChange = e => {
    if (!props.initialized) {
      return;
    }
    e.preventDefault();
    const hashIndex = getHashIndex(window.location.hash, props.slides.length);

    props.actions.dispatchGoToSlide(hashIndex - 1);
  };

  const onIdle = () => {
    if (!insideTopControls) {
      _hideControls();
      setCursorVisible(false);
    }
  };

  const _hideControls = () => {
    setControlsVisible(false);
  };

  const doAutoPlay = () => {
    if (autoTimeout) {
      clearTimeout(autoTimeout);
    }
    const hasMedia = !!(
      props.index < props.slides.length && props.slides[props.index].media
    );
    if (!hasMedia) {
      autoTimeout = setTimeout(doAutoPlayNext, delay * 1000);
    }
  };

  const doAutoPlayNext = () => {
    if (props.index === props.slides.length - 1) {
      if (loop) {
        props.actions.dispatchGoToSlide(0);
      } else {
        window.dispatchEvent(new Event('playerFinished'));
      }
    } else {
      props.actions.dispatchNextSlide();
    }
  };

  const stopAutoPlay = () => {
    if (autoTimeout) {
      clearTimeout(autoTimeout);
    }
  };

  const notifyOtherWindows = () => {
    if (props.slides.length) {
      const data = {
        type: 'slideChanged',
        value: props.slides[props.index].id,
      };
      utils.postMessage(window.parent, data);
      if (window.speakerViewWindow) {
        utils.postMessage(window.speakerViewWindow, data);
      }
    }
  };

  const onTopControlsMouseEnter = () => {
    setInsideTopControls(true);
  };

  const onTopControlsMouseLeave = () => {
    setInsideTopControls(false);
  };
  const onOpenSpeakerView = () => {
    window.speakerViewWindow = props.actions.openSpeakerView(
      window.presentationId,
      props.index + 1,
    );
    if (window.speakerViewWindow) {
      setIsOpenSpeakerView(true);
      window.speakerViewWindow.addEventListener('DOMContentLoaded', () => {
        const lastUpdatedAt = getLastUpdatedAt();
        window.speakerViewWindow.addEventListener('unload', () => {
          window.speakerViewWindow = null;
          setIsOpenSpeakerView(false);
        });
        utils.postMessage(window.speakerViewWindow, {
          type: 'lastUpdatedAt',
          value: lastUpdatedAt,
        });
      });
    } else {
      smalltalk.alert(
        'Oops',
        'An error occurred while opening the speaker view window. Please try again.',
      );
    }
  };

  const getLastUpdatedAt = () => {
    const dates = [];

    dates.push(props.updatedAt);

    props.slides.forEach(slide => {
      dates.push(slide.updatedAt);
    });

    dates.sort((a, b) => {
      const aDate = new Date(a).getTime();
      const bDate = new Date(b).getTime();

      if (aDate < bDate) {
        return 1;
      }
      if (aDate > bDate) {
        return -1;
      }
      return 0;
    });

    return dates[0];
  };

  const onPreviewEnded = () => {
    if (auto) {
      setTimeout(() => {
        doAutoPlayNext();
      }, 1000);
    }
  };

  const presentationHasMedia = !!props.slides.filter(slide => slide.media)
    .length;
  const shouldShowMediaRecorder =
    !isWindowDraft && window.isEditable && !utils.isMobileDevice() && !auto;
  const shouldShowMediaViewer =
    presentationHasMedia &&
    !isWindowDraft &&
    !shouldShowMediaRecorder &&
    !props.isOffline;
  const currentSlideIndex = props.index;
  const slideHasMedia = !!props.slides[currentSlideIndex]?.media;

  const shouldShowControls = props.hideControls
    ? false
    : controlsAlwaysVisible || controlsVisible;

  const size = utils.isEmbed() ? 'medium' : 'big';

  if (!props.initialized) {
    return <AppLoader color="white" />;
  }
  return (
    <ImageContext.Provider value={imageStatus}>
      <div style={styles.app} tabIndex="0">
        <Styles />
        <IdleTimer
          ref={idleTimer}
          element={document}
          onIdle={onIdle}
          debounce={100}
          timeout={1000 * 3}
        />
        <div
          onMouseMove={onMouseMove}
          style={{
            cursor: cursorVisible ? 'auto' : 'none',
          }}
        >
          {showViewer && (
            <>
              {!props.hideControls && (
                <TopControls
                  visible={shouldShowControls}
                  onMouseEnter={onTopControlsMouseEnter}
                  onMouseLeave={onTopControlsMouseLeave}
                  presentationHasMedia={presentationHasMedia}
                  onOpenSpeakerView={onOpenSpeakerView}
                  isOpenSpeakerView={isOpenSpeakerView}
                  clickShare={clickShare}
                />
              )}
              {
                <Viewer
                  auto={auto}
                  dimensions={dimensions}
                  initialized={showViewer}
                  clickShare={clickShare}
                  controlsVisible={controlsVisible}
                  onClick={onClick}
                  onTap={onTap}
                  shouldShowControls={shouldShowControls}
                  notifyOtherWindows={notifyOtherWindows}
                  previousSlideIndex={previousSlideIndex}
                />
              }
              {!props.hideControls && (
                <SlidesList
                  topOffset={utils.isEmbed() ? topOffset : '0px'}
                  controlsVisible={controlsVisible}
                  slidesShowed={showSlides}
                  visible={props.hideControls ? false : !utils.isMobileDevice()}
                />
              )}
              {shouldShowMediaRecorder && (
                <MediaRecorder
                  ref={mediaRecorder}
                  index={props.index}
                  slide={props.slides[props.index]}
                  size={size}
                  controlsVisible={controlsAlwaysVisible}
                  slidesShowed={showSlides}
                />
              )}
              {shouldShowMediaViewer && (
                <MediaViewer
                  ref={mediaViewer}
                  index={props.index}
                  slide={props.slides[props.index]}
                  show={slideHasMedia}
                  size={size}
                  controlsVisible={controlsAlwaysVisible}
                  auto={auto}
                  ready={ready}
                  onPreviewEnded={onPreviewEnded}
                  slidesShowed={showSlides}
                />
              )}
            </>
          )}
        </div>
      </div>
      <ShareDialog visible={visibleShare} onClose={closeShare} />
    </ImageContext.Provider>
  );
};

Container.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  isOffline: PropTypes.bool.isRequired,
  initialized: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  slides: PropTypes.arrayOf(PropTypes.object).isRequired,
  isFullScreen: PropTypes.bool.isRequired,
  updatedAt: PropTypes.string.isRequired,
  team: PropTypes.object,
  user: PropTypes.object,
  transitions: PropTypes.arrayOf(PropTypes.object),
  scrollableViewer: PropTypes.bool,
  hideControls: PropTypes.bool,
  slideChangedBy: PropTypes.string,
  firebaseInitialized: PropTypes.bool,
  publishedVersion: PropTypes.string,
};

Container.defaultProps = {
  user: null,
};

const mapStateToProps = (state, props) => {
  return {
    appInstanceId: state.appReducer.appInstanceId,
    isOffline: state.appReducer.isOffline,
    initialized: state.slideReducer.ready,
    firebaseInitialized: state.slideReducer.firebaseInitialized,
    index: state.slideReducer.index,
    hideControls: state.slideReducer.hideControls,
    scrollableViewer: state.slideReducer.scrollableViewer,
    slideChangedBy: state.slideReducer.slideChangedBy,
    transitionsEnabled: state.slideReducer.transitionsEnabled,
    transitioning: state.slideReducer.transitioning,
    slides: state.slideReducer.slides,
    isFullScreen: state.fullScreenReducer,
    updatedAt: state.slideReducer.updatedAt,
    publishedVersion: state.slideReducer.publishedVersion,
    version: state.slideReducer.version,
    user: state.slideReducer.user,
    team: state.slideReducer.team,
    transitions: state.transitionsReducer,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      preloadSlideImages,
      handleError,
      ...apiActions,
      ...embedActions,
      ...navigationActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Container);
